import {
  AlignLeft01,
  AlignRight01,
  Settings02,
} from "@untitled-ui/icons-react";
import { compact } from "lodash";
import React from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import Tooltip from "@components/tooltip/tooltip";
import client from "@graphql/client";
import { chromeExtensionUrl, role } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { toWithBackground } from "@helpers/helpers";

import { apolloPersistCacheKey } from "../../../helpers/constants";
import { sidebarButtonClassName } from "./sidebar-navigation";

type Props = {
  menuIsCollapsed?: boolean;
  onToggleMenu?: () => void;
};

const SidebarSettings: React.FC<Props> = ({
  menuIsCollapsed,
  onToggleMenu,
}) => {
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const label = useLabel();

  const organizationWithSettingsAccess = currentUser.organizations.edges.find(
    (edge) =>
      (edge?.node || edge?.node?.userMembership) &&
      !edge.node.personal &&
      [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
        edge?.node?.userMembership?.role ?? "",
      ),
  );

  const clearCache = () => {
    client.resetStore();
    client.clearStore();
    localStorage.removeItem(apolloPersistCacheKey);
  };

  const handleClearCache = () => {
    clearCache();
    successNotificationVar({ title: "Cache cleared" });
  };

  const handleLogout = () => {
    clearCache();
    window.location.href = "/logout";
    if (window.Intercom) {
      window.Intercom("shutdown");
    }
  };

  const options = compact([
    {
      type: "link",
      label: "Invite People",
      to: toWithBackground({
        pathname: "/invite",
        location,
      }),
    },
    {
      type: "link",
      label: "Org Chart",
      to: "/org-chart",
    },
    {
      type: "divider",
    },
    {
      type: "link",
      label: "My Account Settings",
      to: "/account",
    },
    organizationWithSettingsAccess?.node?.id && {
      type: "link",
      label: label("organization", { capitalize: true }) + " Settings",
      to: `/settings/organization/${organizationWithSettingsAccess.node.id}/settings`,
    },
    {
      type: "divider",
    },
    {
      type: "link",
      label: "Get the Chrome Extension",
      to: chromeExtensionUrl,
    },
    {
      type: "link",
      label: "Changelog",
      to: "https://www.topicflow.com/changelog/",
    },
    {
      type: "button",
      label: "Clear Cache",
      onClick: handleClearCache,
    },
    {
      type: "divider",
    },
    {
      type: "button",
      label: "Logout",
      onClick: handleLogout,
    },
  ]) as DropdownOptionType[];

  return (
    <div
      className={classNames(
        "flex px-1.5 py-2 w-full justify-between bg-white",
        "fs-unmask",
        menuIsCollapsed ? "flex-col items-center gap-2" : "gap-1",
      )}
    >
      <div className="flex items-center flex-1">
        <Dropdown options={options}>
          <button
            aria-label="Sidebar settings"
            className={classNames(
              "flex cursor-pointer flex-1 items-center min-w-0 text-left gap-1.5 text-gray-700 text-sm hover:bg-blue-100 hover:text-blue-link group",
              menuIsCollapsed ? "justify-center w-full" : "justify-start",
              sidebarButtonClassName,
            )}
          >
            <Settings02
              className={classNames(
                "shrink-0 text-gray-500 group-hover:text-blue-link",
                "h-4 w-4",
              )}
            />
            {!menuIsCollapsed && "Settings"}
          </button>
        </Dropdown>
      </div>
      <div className="flex items-center">
        <Tooltip
          placement="right"
          text={menuIsCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <button
            onClick={onToggleMenu}
            aria-label="Toggle sidebar button"
            className={classNames(
              "cursor-pointer hidden sm:flex items-center justify-center text-gray-500 text-sm hover:bg-gray-100 hover:text-gray-600 shrink-0",
              sidebarButtonClassName,
            )}
          >
            {menuIsCollapsed ? (
              <AlignRight01 className={classNames("h-4 w-4", "shrink-0")} />
            ) : (
              <AlignLeft01 className={classNames("h-4 w-4", "shrink-0")} />
            )}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};
export default SidebarSettings;
